// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";

// Local storage keys

const TOKEN_KEY = "authToken";
const VIEW_MODE_KEY = "viewMode";
const CURRENT_CITY_KEY = "currentCity";
const getToken = () => localStorage.getItem(TOKEN_KEY) || null;
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t || "");
const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const lsGetViewMode = () => localStorage.getItem(VIEW_MODE_KEY) || "user";
const lsSetViewMode = (m) => localStorage.setItem(VIEW_MODE_KEY, m || "user");
const lsClearViewMode = () => localStorage.removeItem(VIEW_MODE_KEY);
const lsGetCurrentCity = () => localStorage.getItem(CURRENT_CITY_KEY) || null;
const lsSetCurrentCity = (city) => {
  if (!city) {
    localStorage.removeItem(CURRENT_CITY_KEY);
    return;
  }
  localStorage.setItem(CURRENT_CITY_KEY, city);
};

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  });

  const [viewMode, setViewModeState] = useState(() => lsGetViewMode());
  const [isLoading] = useState(false);
  const [currentCity, setCurrentCityState] = useState(() => lsGetCurrentCity());

  // ✅ login user
  const loginUser = (userData, token, mode = "user") => {
    localStorage.setItem("user", JSON.stringify(userData));
    if (token) setToken(token);
    lsSetViewMode(mode);
    setUser(userData);
    setViewModeState(mode);
    window.dispatchEvent(new Event("userChange"));
  };

  // ✅ update user info
  const updateUser = (updatedFields) => {
    const next = { ...user, ...updatedFields };
    localStorage.setItem("user", JSON.stringify(next));
    setUser(next);
  };

  // ✅ save auth token
  const saveAuthToken = (token) => setToken(token);

  // ✅ set & get view mode
  const setViewMode = (mode) => {
    const safe = (mode || "").trim() || "user";
    lsSetViewMode(safe);
    setViewModeState(safe);
  };
  const getViewMode = () => viewMode;

  // ✅ logout
  const logoutUser = () => {
    localStorage.removeItem("user");
    clearToken();
    lsClearViewMode();
    lsSetCurrentCity(null);
    setUser(null);
    setViewModeState("user");
    setCurrentCityState(null);
    window.dispatchEvent(new Event("userChange"));
  };

  const setCurrentCity = (city) => {
    const safeCity = city || null;
    lsSetCurrentCity(safeCity);
    setCurrentCityState(safeCity);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        viewMode,
         currentCity,    
        loginUser,
        logoutUser,
        updateUser,
        saveAuthToken,
        getToken,
        setViewMode,
        getViewMode,     
    setCurrentCity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
